import _ from "lodash";
import { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";

export const MAX_SHOWN_ITEMS = 5;

export enum SubscriptionCadence {
    Daily = 1,
    Weekly = 2,
    Monthly = 3,
}

export const filterDisplayTextByKey: Record<string, string> = {
    agentIds: "Agents",
    agentManagerIds: "Agent Managers",
    beginningSentiment: "Starting Sentiment",
    callDirections: "Call Direction",
    clientCallId: "Client Call ID",
    contactTypes: "Contact Type",
    dataLabels: "Conversation Labeling",
    dateReferenceOption: "Date Type",
    eddyEffectStatus: "Eddy Effect Signal",
    embeddedTranscriptionQuery: "AI Powered Search",
    endingSentiment: "Ending Sentiment",
    evaluationTypes: "Evaluation Type",
    favoriteConversations: "Favorite Conversations",
    hierarchyIds: "Hierarchy",
    hipaaCompliance: "HIPAA Compliance",
    languages: "Language",
    maxCallDuration: "Call Duration",
    maxMessageCount: "Message Count",
    mediaTypes: "Media Type",
    modelResults: "Model Results",
    moduleScores: "Module Scores",
    questionResults: "Question Results",
    outlineSegmentFilterItems: "Outline",
    patientJourneyStages: "Patient Journey",
    primaryTopics: "Primary Topic",
    drivers: "Primary Driver",
    rbcFilterItems: "Classifiers",
    safetyEvent: "Safety Event",
    // Membership and sort order only -- nothing renders these strings. The visible text for each
    // filter comes from subscriptionRenderedFilter, which is where the two Theme filters are
    // differentiated and where the "(Deprecated)" marker is gated on the viewer's permissions. This
    // value only keeps the legacy Theme line sorted next to its replacement.
    topics: "Primary Theme (Deprecated)",
    themes: "Primary Theme",
    wordsAndPhrasesItems: "Words or Phrases",
};

export const cadenceOptions = [
    {
        label: "Daily",
        value: SubscriptionCadence.Daily,
        id: "subscribe-to-search-frequency-daily",
    },
    {
        label: "Weekly",
        value: SubscriptionCadence.Weekly,
        id: "subscribe-to-search-frequency-weekly",
    },
    {
        label: "Monthly",
        value: SubscriptionCadence.Monthly,
        id: "subscribe-to-search-frequency-monthly",
    },
];

export const areFiltersEqual = (
    _filters1?: ApplicationFilters,
    _filters2?: ApplicationFilters,
) => {
    if (!_filters1 || !_filters2) {
        return false;
    }
    const ignoredKeys = [
        "endDate",
        "startDate",
        "safetyEvents",
        "adverseEvent",
    ];
    const shouldOmit = (value: unknown, key: string) =>
        value === undefined ||
        value === null ||
        (typeof value === "object" &&
            value !== null &&
            Object.keys(value).length === 0) ||
        ignoredKeys.includes(key);

    const filters1 = _.omitBy(_filters1, shouldOmit);
    const filters2 = _.omitBy(_filters2, shouldOmit);

    if (Object.keys(filters1).length !== Object.keys(filters2).length) {
        return false;
    }
    return Object.keys(filters1).every((key) => {
        if (_.isArray(filters1[key]) && _.isArray(filters2[key])) {
            // Check if arrays have the same elements regardless of order
            return (
                _.xorWith(filters1[key], filters2[key], _.isEqual).length === 0
            );
        }
        return _.isEqual(filters1[key], filters2[key]);
    });
};

export const sortFilters = (filters: string[]) =>
    [...filters].sort((a, b) => {
        const aLabel = filterDisplayTextByKey[a] ?? a;
        const bLabel = filterDisplayTextByKey[b] ?? b;
        return aLabel.localeCompare(bLabel);
    });
